export type SolarLatLngLiteral = { lat: number; lng: number };

export type SolarPanelPlacement = {
  centre: { lat: number; lng: number };
  /** 'PORTRAIT' | 'LANDSCAPE' (GraphQL surfaces this as a plain string) */
  orientation: string;
  segmentIndex: number;
  annualAcKwh: number;
};

export type SolarRoofSegmentInfo = {
  pitchDegrees: number;
  azimuthDegrees: number;
};

const METERS_PER_DEGREE_LAT = 111_320;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

const offsetLatLng = (
  origin: { lat: number; lng: number },
  eastMeters: number,
  northMeters: number
) => ({
  lat: origin.lat + northMeters / METERS_PER_DEGREE_LAT,
  lng:
    origin.lng +
    eastMeters / (METERS_PER_DEGREE_LAT * Math.cos(toRadians(origin.lat))),
});

/**
 * Compute the 4 corners of a panel as lat/lng coordinates.
 *
 * Google's Solar API returns only each panel's centre point; the rectangle is
 * derived from the building-level panel dimensions, the panel orientation and
 * the roof segment's azimuth/pitch. The up-slope edge is foreshortened by
 * cos(pitch) because the map is a top-down ground projection of a tilted
 * plane.
 */
export const computePanelCorners = (
  panel: SolarPanelPlacement,
  segment: SolarRoofSegmentInfo | undefined,
  panelWidthMeters: number,
  panelHeightMeters: number
): Array<{ lat: number; lng: number }> => {
  const azimuth = segment?.azimuthDegrees ?? 180;
  const pitch = segment?.pitchDegrees ?? 0;

  const isPortrait = panel.orientation !== 'LANDSCAPE';
  const alongSlope = isPortrait ? panelHeightMeters : panelWidthMeters;
  const acrossSlope = isPortrait ? panelWidthMeters : panelHeightMeters;

  const halfUp = (alongSlope / 2) * Math.cos(toRadians(pitch));
  const halfAcross = acrossSlope / 2;

  // Unit vectors (east, north) for the down-slope (azimuth) direction and the
  // across-slope direction (perpendicular, azimuth + 90°).
  const azimuthRad = toRadians(azimuth);
  const down = { east: Math.sin(azimuthRad), north: Math.cos(azimuthRad) };
  const acrossRad = toRadians(azimuth + 90);
  const across = { east: Math.sin(acrossRad), north: Math.cos(acrossRad) };

  const corners: Array<[number, number]> = [
    [-1, -1],
    [-1, 1],
    [1, 1],
    [1, -1],
  ];

  return corners.map(([alongSign, acrossSign]) =>
    offsetLatLng(
      panel.centre,
      alongSign * halfUp * down.east + acrossSign * halfAcross * across.east,
      alongSign * halfUp * down.north + acrossSign * halfAcross * across.north
    )
  );
};

const approxDistanceMeters = (a: SolarLatLngLiteral, b: SolarLatLngLiteral) => {
  const dLat = (b.lat - a.lat) * METERS_PER_DEGREE_LAT;
  const dLng =
    (b.lng - a.lng) *
    METERS_PER_DEGREE_LAT *
    Math.cos(toRadians((a.lat + b.lat) / 2));
  return Math.hypot(dLat, dLng);
};

const lerpLatLng = (
  a: SolarLatLngLiteral,
  b: SolarLatLngLiteral,
  t: number
): SolarLatLngLiteral => ({
  lat: a.lat + (b.lat - a.lat) * t,
  lng: a.lng + (b.lng - a.lng) * t,
});

/** Aim for roughly square ~0.36 m photovoltaic cells. */
const TARGET_CELL_METERS = 0.36;
const MAX_CELLS_PER_AXIS = 6;

/**
 * Interior grid lines that make a placed panel read as a photovoltaic cell
 * matrix instead of a flat fill. Given the panel's 4 ring-ordered corners,
 * returns endpoint pairs (each pair = one polyline) that divide the quad into
 * roughly square cells, aligned to the panel's own rotation. Cell counts are
 * derived from the quad's real edge lengths so portrait/landscape panels both
 * get a sensible matrix.
 */
export const computePanelCellGridLines = (
  corners: SolarLatLngLiteral[]
): Array<[SolarLatLngLiteral, SolarLatLngLiteral]> => {
  if (corners.length !== 4) return [];
  const [c0, c1, c2, c3] = corners;
  const lines: Array<[SolarLatLngLiteral, SolarLatLngLiteral]> = [];

  const addLinesBetweenOppositeEdges = (
    edgeAStart: SolarLatLngLiteral,
    edgeAEnd: SolarLatLngLiteral,
    edgeBStart: SolarLatLngLiteral,
    edgeBEnd: SolarLatLngLiteral
  ) => {
    const cells = Math.min(
      MAX_CELLS_PER_AXIS,
      Math.max(
        2,
        Math.round(
          approxDistanceMeters(edgeAStart, edgeAEnd) / TARGET_CELL_METERS
        )
      )
    );
    for (let i = 1; i < cells; i++) {
      const t = i / cells;
      lines.push([
        lerpLatLng(edgeAStart, edgeAEnd, t),
        lerpLatLng(edgeBStart, edgeBEnd, t),
      ]);
    }
  };

  // Ring c0→c1→c2→c3: opposite edge pairs are (c0→c1, c3→c2) and
  // (c1→c2, c0→c3). Lines joining matching fractions of an opposite pair
  // cross the quad parallel to the other pair — together, a cell matrix.
  addLinesBetweenOppositeEdges(c0, c1, c3, c2);
  addLinesBetweenOppositeEdges(c1, c2, c0, c3);
  return lines;
};

/** Ray casting in lat/lng: a roof is small enough for the distortion not to matter. */
const pointInRing = (
  point: SolarLatLngLiteral,
  ring: SolarLatLngLiteral[]
): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    const straddles = a.lat > point.lat !== b.lat > point.lat;
    if (
      straddles &&
      point.lng <
        ((b.lng - a.lng) * (point.lat - a.lat)) / (b.lat - a.lat) + a.lng
    ) {
      inside = !inside;
    }
  }
  return inside;
};

/**
 * The panel under a point on the map, or null for bare roof.
 *
 * Google Maps hit-tests by z-order, so a polygon drawn over the panels (the
 * selected face's editable boundary) takes their clicks. Handing that click
 * back to the panel underneath keeps "click a panel to include or exclude it"
 * working while the face stays draggable. `preferFaceIndex` breaks ties
 * towards the face being edited when two faces' panels overlap in plan.
 */
export const panelIndexAtPoint = (
  panels: Array<{ corners: SolarLatLngLiteral[]; faceIndex?: number | null }>,
  point: SolarLatLngLiteral,
  preferFaceIndex?: number | null
): number | null => {
  let fallback: number | null = null;
  for (let index = 0; index < panels.length; index++) {
    const panel = panels[index];
    if (panel.corners.length < 3 || !pointInRing(point, panel.corners))
      continue;
    if (preferFaceIndex == null || panel.faceIndex === preferFaceIndex) {
      return index;
    }
    if (fallback == null) fallback = index;
  }
  return fallback;
};
